import { Box, Button, CardMedia, CircularProgress, Grid, IconButton, Skeleton, Tab, Tabs, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import CustomNavChart from '../../dashboard/chart/CustomNavChart';
import { AUMActiveIcon, DownwardNegativeIcon, ExitLoadIcon, InfoIconOutlined, KotakIcon, LumpsumSIPIcon, MutualFundsActiveIcon, UpWardPositiveIcon } from '../../../assets/SvgIcons';
import { checkImageExists, filterDataPointsForNav, formatCurrency, formatDate, isModuleAllowed, toINR } from '../../../utils/utilityFunctions';
import FundManagerDetails from './FundManagerDetails';
import { CompaniesTable } from '../../dashboard/table/CompaniesTable';
import { SectorsTable } from '../../dashboard/table/SectorTable';
import { useHistory } from "react-router";
import React from 'react';
import { useDispatch, useSelector } from "react-redux";
import { GetHistoricalNavResponseBody, GetIndividualFundDetailsResponseBody, HistoricalNavItem } from '../../../redux-store/types/investorProfile';
import { useParams } from 'react-router-dom';
import { getHistoricalNavDetails, getIndividualFundDetails } from '../../../redux-store/actions/investorProfile';
import BreadCrum from '../../../lib/BreadCrum';
import { RootStateType } from '../../../redux-store/reducers';
import { ErrorText } from '../../dashboard/chart/CustomeLineChart';
import { showError } from '../../../redux-store/actions/auth';
import { HELDAWAY_PORTFOLIO_STATUS, USER_ROLES } from '../../../utils/constant';


interface Companies {
    companyName: string;
    allocation: number;
}

interface Sectors {
    sectorName: string;
    allocation: number;
}

interface Holdings {
    categoryType: string;
    categoryPercentage: number;
    companies: Array<Companies>;
    sectors: Array<Sectors>;
}

interface OtherFundsManagedProps {
    name: string;
}
interface FundManagerProps {
    name: string;
    serviceStartPeriod: string | null;
    servicePeriod: string;
    education: string;
    experience: string;
    otherFundsManaged: Array<OtherFundsManagedProps>
}
interface FundDetails {
    name: string;
    fundCategory: string;
    riskType: string;
    minimumLumpsum: number;
    minSIP: number;
    AUM: number;
    expenseRatio: number;
    exitLoad: number;
    holdings: Array<Holdings>;
    fundManagers: Array<FundManagerProps>
}

interface Params {
    investorId: string;
    individualFundId: string
}

export default function IndividualFundDetails(): JSX.Element {
    const history = useHistory()
    const { pathname: pathName, state } = history.location;
    const dispatch = useDispatch();
    const { individualFundId: schemeId } = useParams<Params>();
    const { selectedSchemes, clientCode } = (state as { selectedSchemes?: Array<{ [key: string]: any }>, clientCode?: string }) ?? []
    const {
        role = '',
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { isPortfolioHeldaway, selectedPortfolio, bseTxnStatus } = useSelector((store: RootStateType) => store.selectedPortfiolio);

    const theme = useTheme();

    const [fundDetails, setFundDetails] = useState<GetIndividualFundDetailsResponseBody>({
        name: '',
        fundCategory: '',
        fundTypeCategory: '',
        currentNav: 0,
        gainLossPercentage: 0,
        riskType: '',
        minimumLumpsum: 0,
        minSip: 0,
        AUM: 0,
        expenseRatio: 0,
        exitLoad: 0,
        exitLoadNote: '',
        holdings: [],
        fundManagers: [],
        historical_nav: [],
        amcId: '',
        navDate: '',
        isinCode: ''
    })
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState(0);
    const [selectedTab, setSelectedTab] = useState('companies');
    const [navRes, setNavRes] = useState<GetHistoricalNavResponseBody>({
        message: '',
        isSuccess: false,
        result: [],
    });
    const [allFundData, setAllFundData] = useState<Array<HistoricalNavItem>>([]);

    const [currentFundData, setCurrentFundData] = useState<Array<HistoricalNavItem>>([]);

    const [showAllCompanies, setShowAllCompanies] = useState(false);
    const [navChartBtn, setNavChartBtn] = useState('Inception');


    const handleShowAllCompaniesToggle = () => {
        setShowAllCompanies(!showAllCompanies);
    };

    const navChartButtons = [{
        type: 'month',
        count: 1,
        text: '1M'
    }, {
        type: 'month',
        count: 6,
        text: '6M'
    }, {
        type: 'year',
        count: 1,
        text: '1Y'
    }, {
        type: 'year',
        count: 3,
        text: '3Y'
    }, {
        type: 'year',
        count: 5,
        text: '5Y'
    },
    {
        type: 'all',
        text: 'Inception'
    }]

    interface IndNavBtnProps {
        type: string,
        count?: number,
        text: string
    }

    const sortHoldingsByAllocation = (fundData: GetIndividualFundDetailsResponseBody) => {
        fundData?.holdings?.forEach(holding => {
            holding.companies.sort((a, b) => b.allocation - a.allocation);
            holding.sectors.sort((a, b) => b.allocation - a.allocation);
        });
        return fundData;
    };

    const fetchIndividualFundData = async () => {

        try {
            setLoading(true);
            const individualFundPayload = {
                schemeId: schemeId,
            }

            const historicalPayload = {
                schemeId: selectedSchemes?.[0]?.mPowerScheme ?? ''
            }

            const [individualFundRes, historicalNavRes] = await Promise.allSettled([
                (dispatch(getIndividualFundDetails(individualFundPayload)) as unknown as Promise<GetIndividualFundDetailsResponseBody>),
                (dispatch(getHistoricalNavDetails(historicalPayload)) as unknown as Promise<GetHistoricalNavResponseBody>)
            ]);

            setFundDetails(sortHoldingsByAllocation(individualFundRes?.status === 'fulfilled' ? individualFundRes?.value : fundDetails));

            setNavRes(historicalNavRes?.status === 'fulfilled' ? historicalNavRes?.value : navRes);
            setAllFundData(historicalNavRes?.status === 'fulfilled' ? historicalNavRes?.value?.result : []);
            setCurrentFundData(historicalNavRes?.status === 'fulfilled' ? (historicalNavRes?.value?.result?.length > 0 ? [...historicalNavRes?.value?.result] : []) : [])
        } catch (e) {
            console.log("ERROR", e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchIndividualFundData();
    }, [])

    const handleChange = (newValue: number) => {
        setValue(newValue);
    };



    const handleNavChartBtn = (btn: string) => {
        setNavChartBtn(btn);
        const temp_filteredData = filterDataPointsForNav(allFundData, btn);
        setCurrentFundData(temp_filteredData?.length > 0 ? [...temp_filteredData] : []);

    }

    const handleProceedButton = () => {
        if (isPortfolioHeldaway) {
            dispatch(showError(HELDAWAY_PORTFOLIO_STATUS))
            return
        }

        history.push(`${pathName}/investment-details`, { selectedSchemes, clientCode })
    }

    const initialRouter = pathName.split('/').filter((item) => item !== '')

    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        {
            name: initialRouter[1] + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Mutual Fund',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode
            },
            search: "?tab=Overview"
        },
        {
            name: 'Explore Mutual Funds',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode
            }
        },
        {
            name: fundDetails?.name || 'Loading...',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}/${initialRouter[4]}`,
            state: {
                clientCode
            }
        }

    ] : [])

    const investorRouterPath = [
        {
            name: 'Dashboard' + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
        },
        {
            name: 'Mutual Fund',
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode
            },
            search: "?tab=Overview"
        },
        {
            name: 'Explore Mutual Funds',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode
            }
        },
        {
            name: fundDetails?.name || 'Loading...',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode
            }
        }
    ]

    const amcIds = ['BDOTR', 'BPIAMC']
    const checkAmcId = amcIds.includes(fundDetails?.amcId ?? '') ? fundDetails?.amcId?.toLowerCase() : fundDetails?.amcId;

    return (
        <>
            <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />

            <Grid container columnSpacing={2} mt={'15px'}>

                {/* Main Grid */}
                <Grid item xs={12} lg={8}>

                    {/* Fund Details and NAV chart */}
                    <Box component={'section'} sx={(theme: Theme) => (
                        { padding: '35px 30px', borderRadius: '20px', background: theme.palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', mb: "25px" }
                    )}>
                        <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', columnGap: '15px', mb: '45px' }}>
                            {loading
                                ? <Skeleton animation="wave" width={50} height={50} variant="rectangular" />
                                :
                                <Box
                                    component="img"
                                    alt={fundDetails?.name ?? ""}
                                    src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                                    sx={{ width: "50px", height: "50px", objectFit: "contain" }}
                                />
                            }


                            <Box component={'div'}>
                                {loading ?
                                    <Skeleton animation="wave" width={"300px"} />
                                    :
                                    <Typography variant='h3' sx={{ letterSpacing: '0.02px' }} >
                                        {fundDetails?.name ?? ""}
                                    </Typography>}
                                {loading ?
                                    <Box>
                                        <Skeleton animation="wave" width={'200px'} />
                                    </Box>
                                    :
                                    <Box component={'div'} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {fundDetails?.fundCategory === "NULL" || fundDetails?.fundCategory === null ?
                                            <></>
                                            :
                                            <Typography component={'span'} variant='body_500' sx={{ color: 'text.secondary', letterSpacing: '0.02px' }} >
                                                {`${fundDetails?.fundCategory ?? ""}`}
                                            </Typography>
                                        }
                                        <Box component={'span'} sx={(theme: Theme) => (
                                            {
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '5px',
                                                height: '5px',
                                                borderRadius: '50%',
                                                mx: '6px',

                                                background: theme.palette.text.secondary,
                                            }
                                        )}></Box>
                                        {fundDetails?.fundTypeCategory === "NULL" || fundDetails?.fundTypeCategory === null ?
                                            <></>
                                            :
                                            <Typography component={'span'} variant='body_500' sx={{ color: 'text.secondary', letterSpacing: '0.02px' }} >{`${fundDetails?.fundTypeCategory ?? ""}`}
                                            </Typography>
                                        }
                                        {fundDetails?.riskType === "NULL" || fundDetails?.riskType === null ? <></> : <>
                                            <Box component={'span'} sx={(theme: Theme) => (
                                                {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '5px',
                                                    height: '5px',
                                                    borderRadius: '50%',
                                                    mx: '6px',
                                                    background: theme.palette.text.secondary,
                                                }
                                            )} />
                                            <Typography component={'span'} variant='body_500' sx={(theme: Theme) => (
                                                { color: theme.palette.text.secondary, letterSpacing: '0.02px' }
                                            )} >{`${fundDetails?.riskType ?? ""} Risk`}</Typography>
                                        </>}
                                    </Box>}
                                {loading ? <Skeleton animation="wave" width="30%" /> : <Typography variant='body_500' sx={{ color: 'text.secondary' }}>{`ISIN: ${fundDetails?.isinCode ?? ""}`}</Typography>}
                            </Box>
                        </Box>
                        <Box component={'div'}>
                            {loading ? <Skeleton animation="wave" width="30%" /> : <Box sx={{ display: 'flex', alignItems: 'center' }}>

                                <Box sx={(theme: Theme) => (
                                    {
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '11px',
                                        height: '11px',
                                        borderRadius: '50%',
                                        mx: '4px',
                                        background: theme.palette.primary[500],
                                    }
                                )}></Box>
                                <Typography variant='body_500' sx={{ color: 'text.secondary' }}>{`NAV: ${fundDetails?.navDate ?? ""}`}</Typography>
                            </Box>}
                            {loading ? <Box>
                                <Skeleton animation="wave" width="30%" sx={{ mb: '45px' }} />
                            </Box> : <Box component={'div'} style={{ display: 'inline-flex', alignItems: 'center', marginBottom: '45px' }}>

                                <Typography component={'span'} variant='body_600' sx={{ color: 'text.primary', mr: '4px' }}>
                                    {toINR({ value: fundDetails?.currentNav ?? 0, locale, currencyCode })}
                                </Typography>
                                {Number(fundDetails?.gainLossPercentage ?? 0) >= 0
                                    ?
                                    <UpWardPositiveIcon />
                                    : <DownwardNegativeIcon />
                                }
                                <Typography variant='body_500' sx={(theme: Theme) => (
                                    {
                                        color: Number(fundDetails?.gainLossPercentage ?? 0) >= 0
                                            ? theme.palette.success[500]
                                            : theme.palette.error[500]
                                    }
                                )} component={'span'}>
                                    {`${fundDetails?.gainLossPercentage ?? 0}%`}
                                </Typography>
                            </Box>}
                        </Box>
                        <Box>
                            {loading ? <Box>

                                {/* Y-axis skeleton */}
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',

                                    }}
                                >
                                    <Skeleton variant="rectangular" width={10} height={150} />
                                    <Skeleton variant="rectangular" width={10} height={150} />
                                    <Skeleton variant="rectangular" width={10} height={150} />
                                </Box>

                                {/* X-axis skeleton */}
                                <Box
                                    style={{
                                        display: 'flex',
                                    }}
                                >
                                    <Skeleton variant="rectangular" width={'100%'} height={10} />
                                    <Skeleton variant="rectangular" width={'100%'} height={10} />
                                    <Skeleton variant="rectangular" width={'100%'} height={10} />
                                </Box>
                            </Box> : <>
                                <Box>
                                    {(!navRes?.isSuccess || !currentFundData?.length) ? <ErrorText /> : <CustomNavChart data={currentFundData?.length ? currentFundData : []} />}
                                </Box>
                                <Box sx={{ mt: '45px', display: 'flex', justifyContent: 'center', alignItems: 'center', columnGap: '15px', overflowX: 'auto' }} >
                                    {navChartButtons?.map((individualBtn: IndNavBtnProps, index: number) => {
                                        return (
                                            <Button key={index} sx={(theme: Theme) => (
                                                {
                                                    ...theme.typography.body_500, letterSpacing: '0.02px', borderRadius: '7px', padding: '5px 15px',
                                                    mb: 1,
                                                    minWidth: '80px',
                                                    border: 'none', cursor: 'pointer', color: { color: navChartBtn === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary }, '&:hover': {
                                                        border: 'none',
                                                    }
                                                }
                                            )} variant={navChartBtn === individualBtn?.text ? 'contained' : 'outlined'}
                                                onClick={() => handleNavChartBtn(individualBtn?.text)}
                                            >{individualBtn?.text}</Button>
                                        )
                                    })}
                                </Box>
                            </>}
                        </Box>
                    </Box>

                    {/* Holdings */}
                    <Box component={'section'} sx={(theme: Theme) => (
                        { padding: '35px 30px', borderRadius: '20px', background: theme.palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', mb: '25px' }
                    )}>
                        {loading ?
                            <Skeleton animation="wave" width={'30%'} sx={{ mb: '35px' }} />
                            :
                            <Typography variant='h2' sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                mb: '35px'
                            })}>
                                Holdings
                            </Typography>
                        }

                        <Box component={'div'} sx={(theme: Theme) => (
                            {

                                border: loading ? 0 : `1px solid ${theme.palette.text.secondary}B2`,
                                borderRadius: '55px',
                                mb: '45px'

                            }
                        )}>
                            {loading ? <Box sx={{ display: 'flex' }}>
                                <Skeleton animation="wave" variant='rectangular' width={'100%'} height={'30px'} sx={{ borderRadius: '55px', mr: '10px' }} />
                                <Skeleton animation="wave" variant='rectangular' width={'100%'} height={'30px'} sx={{ borderRadius: '55px' }} />
                            </Box> :
                                <Tabs
                                    value={value}
                                    onChange={() => handleChange(value === 0 ? 1 : 0)}
                                    variant="fullWidth"
                                    aria-label="Customized tabs example"
                                    sx={{

                                        '& .MuiTabs-indicator': {
                                            display: 'none'
                                        }
                                    }}
                                >
                                    <Tab
                                        label={<Typography variant='body_500' sx={(theme: Theme) => (
                                            {
                                                color: value === 0 ? theme.palette.common.white : theme.palette.text.secondary,
                                                fontSize: '18px',
                                                textTransform: 'capitalize'
                                            }
                                        )}>{`${fundDetails?.holdings?.[0]?.categoryType ?? ""} ${fundDetails?.holdings?.[0]?.categoryPercentage > 0 ? !Number.isNaN(Number(fundDetails?.holdings?.[0]?.categoryPercentage ?? 0)) ?
                                            Math.round((fundDetails?.holdings?.[0]?.categoryPercentage ?? 0) * 100) / 100 : '0' : 0}%`}</Typography>}
                                        sx={(theme: Theme) => (
                                            {
                                                backgroundColor: value === 0 ? theme.palette.primary[500] : theme.palette.common.white,
                                                borderRadius: "55px",

                                            }
                                        )}
                                    />
                                    <Tab
                                        label={<Typography variant='body_500' sx={(theme: Theme) => (
                                            {
                                                color: value === 1 ? theme.palette.common.white : theme.palette.text.secondary,
                                                fontSize: '18px',
                                                textTransform: 'capitalize'
                                            }
                                        )}>
                                            {`${fundDetails?.holdings?.[1]?.categoryType ?? ""} ${fundDetails?.holdings?.[1]?.categoryPercentage > 0 ? !Number.isNaN(Number(fundDetails?.holdings?.[1]?.categoryPercentage ?? 0))
                                                ?
                                                Math.round((fundDetails?.holdings?.[1]?.categoryPercentage ?? 0) * 100) / 100 : '0'
                                                : 0}%`
                                            }
                                        </Typography>
                                        }
                                        sx={(theme: Theme) => (
                                            {
                                                backgroundColor: value === 1 ? theme.palette.primary[500] : theme.palette.common.white,
                                                fontSize: '18px',
                                                borderRadius: "55px",
                                            }
                                        )}
                                    />
                                </Tabs>}
                        </Box>
                        {loading ? <>
                            <Box sx={{ display: 'flex' }}>
                                <Skeleton animation="wave" variant='rectangular' width={'20%'} height={'30px'} sx={{ mr: '5px' }} />
                                <Skeleton animation="wave" variant='rectangular' width={'20%'} height={'30px'} sx={{ mr: '5px' }} />
                            </Box>
                        </> : <Box component={'section'} sx={{ mb: '45px' }}>
                            <Box component={'div'} sx={(theme: Theme) => ({ borderBottom: `1px solid ${theme.palette.grey[200]}` })}>
                                <Button
                                    variant={selectedTab === 'companies' ? 'contained' : 'outlined'}
                                    onClick={() => setSelectedTab('companies')}
                                    sx={(theme: Theme) => ({
                                        padding: '5px 15px', borderRadius: '7px 7px 0 0', mr: '6px', border: 'none', cursor: 'pointer', background: selectedTab === 'companies' ? theme.palette.primary[500] : theme.palette.grey[50], '&:hover': {
                                            border: 'none',

                                        },
                                    })}

                                >
                                    <Typography variant='body_500' sx={(theme: Theme) => ({
                                        color: selectedTab === 'companies' ? theme.palette.common.white : theme.palette.text.secondary
                                    })}>Companies</Typography>
                                </Button>
                                <Button
                                    variant={selectedTab === 'sectors' ? 'contained' : 'outlined'}
                                    onClick={() => setSelectedTab('sectors')}
                                    sx={(theme: Theme) => ({
                                        padding: '5px 15px', borderRadius: '7px 7px 0 0', border: 'none', cursor: 'pointer', background: selectedTab === 'sectors' ? theme.palette.primary[500] : theme.palette.grey[50], '&:hover': {
                                            border: 'none',

                                        },
                                    })}
                                >
                                    <Typography variant='body_500' sx={(theme: Theme) => ({
                                        color: selectedTab === 'sectors' ? theme.palette.common.white : theme.palette.text.secondary,

                                    })}>Sectors</Typography>
                                </Button>
                            </Box>
                        </Box>}

                        <Box component={'section'}>
                            {value === 0 && (selectedTab === 'companies' ? (
                                <CompaniesTable data={showAllCompanies ? fundDetails?.holdings?.[0]?.companies : fundDetails?.holdings?.[0]?.companies?.slice(0, 5)} showAll={showAllCompanies} onShowAllToggle={handleShowAllCompaniesToggle} loading={loading} />
                            ) : (
                                <SectorsTable data={showAllCompanies ? fundDetails?.holdings?.[0]?.sectors : fundDetails?.holdings?.[0]?.sectors?.slice(0, 5)} showAll={showAllCompanies} onShowAllToggle={handleShowAllCompaniesToggle} loading={loading} />
                            ))}
                            {value === 1 && (selectedTab === 'companies' ? (
                                <CompaniesTable data={showAllCompanies ? fundDetails?.holdings?.[1]?.companies : fundDetails?.holdings?.[1]?.companies?.slice(0, 5)} showAll={showAllCompanies} onShowAllToggle={handleShowAllCompaniesToggle} loading={loading} />
                            ) : (
                                <SectorsTable data={showAllCompanies ? fundDetails?.holdings?.[1]?.sectors : fundDetails?.holdings?.[1]?.sectors?.slice(0, 5)} showAll={showAllCompanies} onShowAllToggle={handleShowAllCompaniesToggle} loading={loading} />))}

                        </Box>

                    </Box>

                    {/* Fund Managers */}
                    <Box component={'section'} sx={(theme: Theme) => (
                        { padding: '35px 30px', borderRadius: '20px', background: theme.palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)' }
                    )}>
                        {loading ? <Skeleton animation="wave" width={'30%'} /> : <Typography variant='h2' sx={(theme: Theme) => ({ color: theme.palette.text.primary, letterSpacing: '0.01px', mb: '35px' })}>Fund Management</Typography>}

                        <Box component={'div'}>
                            {fundDetails?.fundManagers?.map((fundManager: FundManagerProps, index: number) => (
                                <React.Fragment key={index}>
                                    <FundManagerDetails key={index} fundManager={fundManager} loading={loading} />
                                    {fundDetails?.fundManagers?.length && index !== fundDetails?.fundManagers?.length - 1 && <hr />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>


                </Grid>

                {/* Overview */}
                <Grid item xs={12} lg={4} sx={{ mt: { xs: '25px', lg: '0' } }}>
                    <div style={{ position: 'sticky', top: 0 }}>
                        <Box component={'section'} sx={(theme: Theme) => (
                            { padding: '25px 30px 35px 30px', borderRadius: '20px', background: theme.palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)' }
                        )}>
                            {loading ? <Skeleton animation="wave" width={'50%'} height={'40px'} sx={{ mb: '35px' }} /> : <Typography variant='h2' sx={{ letterSpacing: '0.01px', color: 'text.primary', mb: '35px' }}>Overview</Typography>}
                            <Grid container gap="35px">
                                <Grid item xs={12}>
                                    <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', columnGap: '7px' }}>
                                            <LumpsumSIPIcon />
                                            <Typography component={'span'} variant='body_500' sx={{ color: 'text.primary', letterSpacing: '0.02px' }}>Min Lumpsum/SIP</Typography>
                                        </Box>}
                                        <Box sx={(theme: Theme) => ({
                                            [theme.breakpoints.down(531)]: { mt: 1 }
                                        })}>
                                            {loading ? <Box>
                                                <Skeleton animation="wave" width={'150px'} />
                                            </Box> : <Typography variant='body_600' sx={{ color: 'text.primary', letterSpacing: '0.01px' }}>{`${formatCurrency({ value: fundDetails?.minimumLumpsum ?? 0, locale, currencyCode })}/ ${formatCurrency({ value: fundDetails?.minSip ?? 0, locale, currencyCode })}`}</Typography>}
                                        </Box>

                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', columnGap: '7px' }}>
                                            <AUMActiveIcon />
                                            <Typography component={'span'} variant='body_500' sx={{ color: 'text.primary', letterSpacing: '0.02px' }}>AUM</Typography>
                                        </Box>}
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box>
                                            <Typography variant='body_600' sx={{ color: 'text.primary', letterSpacing: '0.01px' }}>{`${formatCurrency({ value: fundDetails?.AUM ?? 0, locale, currencyCode })}`}</Typography>
                                        </Box>}

                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', columnGap: '7px' }}>
                                            <MutualFundsActiveIcon />
                                            <Typography component={'span'} variant='body_500' sx={{ color: 'text.primary', letterSpacing: '0.02px' }}>Expense Ratio</Typography>

                                            <Tooltip
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            margin: "0px !important",
                                                            backgroundColor: "transparent"
                                                        }
                                                    }
                                                }}
                                                title={
                                                    <div
                                                        style={{
                                                            borderRadius: '10px', backgroundColor: '#0F131F', padding: '15px'
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                color: theme.palette.primary[500],
                                                                fontWeight: '600',
                                                                fontSize: '16px'
                                                            }}
                                                        >
                                                            {`${fundDetails?.expenseRatio ?? 0}%`}
                                                        </span>
                                                        <br />
                                                        <span style={{ ...theme.typography.subtitle_500, fontFamily: theme.typography.fontFamily }}>Expense ratio is the the fees charged by mutual fund companies to manage the mutual fund.</span>
                                                    </div>
                                                }
                                            >
                                                <IconButton>
                                                    <InfoIconOutlined />
                                                </IconButton>
                                            </Tooltip>


                                        </Box>}
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box>
                                            <Typography variant='body_600' sx={{ color: 'text.primary', letterSpacing: '0.01px' }}>{`${fundDetails?.expenseRatio ?? 0}%`}</Typography>
                                        </Box>}

                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', columnGap: '7px' }}>
                                            <ExitLoadIcon />
                                            <Typography component={'span'} variant='body_500' sx={{ color: 'text.primary', letterSpacing: '0.02px' }}>Exit Load</Typography>
                                            <Tooltip
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            margin: "0px !important",
                                                            backgroundColor: "transparent"
                                                        }
                                                    }
                                                }}
                                                title={
                                                    <div style={{ borderRadius: '10px', backgroundColor: '#0F131F', padding: '15px' }}>
                                                        <span style={{ color: theme.palette.primary[500], fontWeight: '600', fontSize: '16px' }}>{`${fundDetails?.exitLoad ?? 0}%`}</span>
                                                        <br />
                                                        <span style={{ ...theme.typography.subtitle_500, fontFamily: theme.typography.fontFamily }}>{fundDetails?.exitLoadNote ?? ''}</span>
                                                        <br />
                                                        <span style={{ ...theme.typography.subtitle_500, fontFamily: theme.typography.fontFamily }}>Exit load is the fees charged by AMC&apos;s for premature redemption of mutual fund units.</span>
                                                    </div>
                                                } >
                                                <IconButton>
                                                    <InfoIconOutlined />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>}
                                        {loading ? <Box>
                                            <Skeleton animation="wave" width={'150px'} />
                                        </Box> : <Box>
                                            <Typography variant='body_600' sx={{ color: 'text.primary', letterSpacing: '0.01px' }}>{`${fundDetails?.exitLoad ?? 0}%`}</Typography>

                                        </Box>}

                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    {loading ? "" : <hr />}
                                </Grid>
                                <Grid item xs={12}>
                                    {loading
                                        ? <Box>
                                            <Skeleton animation="wave" variant='rectangular' width={'100%'} height={'40px'} sx={{ borderRadius: '10px' }} />
                                        </Box>
                                        :
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            sx={
                                                { width: '100%', borderRadius: '10px', padding: '10px' }
                                            }
                                            onClick={handleProceedButton}
                                            disabled={
                                                !isModuleAllowed({ value: 'MFOrders', compareArr: permissions, objKey: "permissonCode" }) &&
                                                !isModuleAllowed({ value: 'OrderRecommend', compareArr: permissions, objKey: "permissonCode" })
                                            }
                                        >
                                            Proceed
                                        </Button>
                                    }
                                </Grid>
                            </Grid>
                        </Box>
                    </div>
                </Grid>
            </Grid>
        </>
    )
}